import './styles/TermsConditions_W.scss';

import { useMemo } from 'react';

import { useClassHelper } from '../../../../hooks/useClassHelper';
import { DataDogLogger } from '../../../../hooks/useDataDogLogger';
// Local Helper
import { TextEditorContent, WebsheetTemplateProps } from '../../../../types';
import { TermsAndConditionsBase } from '../../components/TermsAndConditionsBase';
import { useGetStyles } from '../../hooks/useGetStyles';
import { useLocalisation } from '../../hooks/useLocalisation';
import { watchFamilyPlanFields } from './constants';
import { resolveTermsText } from './helpers';

const componentName = 'c-TermsConditionsW';

interface Props extends WebsheetTemplateProps {
  onClose?: () => void;
  'data-dd-action-name'?: string;
  warningLogger?: DataDogLogger;
  isLinked?: boolean;
}

export const TermsConditionsW = (props: Props) => {
  const {
    values,
    currentLocale,
    onClose,
    isPortalPreview,
    warningLogger,
    selectedField,
    isLinked = false,
    'data-dd-action-name': dataDdActionName,
  } = props;
  const getStyles = useGetStyles({ values });
  const { localisations } = values;
  const c = useClassHelper(componentName);

  const localisation = useLocalisation(
    localisations,
    currentLocale,
    warningLogger,
  );

  const handleClose = () => {
    onClose?.();
  };

  // In portal preview there is no `subscription_type`, so the carrier previews the
  // Family Plan (linked) T&C variant by selecting one of its localisation fields.
  const isFamilyPlanSelected = watchFamilyPlanFields.includes(
    selectedField?.field as (typeof watchFamilyPlanFields)[number],
  );
  const isLinkedView = isLinked || (isPortalPreview && isFamilyPlanSelected);

  const { title, content, back_button } = useMemo(() => {
    const defaultValues = {
      title: 'Terms & Conditions',
      content: [],
      back_button: 'Back',
    };

    if (localisation?.['t&c']) {
      const resolved = resolveTermsText(
        isLinkedView,
        localisation['t&c'] as Record<string, unknown>,
      );
      return {
        title: resolved.title || defaultValues.title,
        content:
          (resolved.content as TextEditorContent[]) || defaultValues.content,
        back_button: resolved.back_button || defaultValues.back_button,
      };
    }

    if (props?.text) {
      const resolved = resolveTermsText(
        isLinkedView,
        props.text as Record<string, unknown>,
      );
      return {
        title: resolved.title || defaultValues.title,
        content:
          (resolved.content as TextEditorContent[]) || defaultValues.content,
        back_button: resolved.back_button || defaultValues.back_button,
      };
    }

    return defaultValues;
  }, [localisation, props, isLinkedView]);

  return (
    <TermsAndConditionsBase
      getStyles={getStyles}
      isPortalPreview={isPortalPreview}
      styling={{
        logoUrl: values?.logoUrl,
        title: {
          text: title || '',
          fontFamily: values?.fontTitle,
        },
        content: {
          text: content,
          valueFontParagraph: values?.fontParagraph,
        },
      }}
      footer={
        <footer>
          <button
            type="button"
            className={c('__highlight')}
            onClick={handleClose}
            style={{
              color: values?.colorLink,
            }}
            data-dd-action-name={dataDdActionName}
          >
            {back_button || 'Back'}
          </button>
        </footer>
      }
    />
  );
};
